import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { timezoneOptions } from '../utils/kernelTimezones';
import { DEFAULT_TIMEZONE } from '../utils/kernelTimezone';
import './kernelTimezoneSelect.css';

// "+01:00": utile per orientarsi tra i fusi quando si conosce lo scarto orario ma non il nome
// IANA. Dipende anche dall'ora legale, quindi è quello di "adesso", non un valore fisso per il fuso.
function zoneOffsetText(zone) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' }).formatToParts(new Date());
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    const m = raw.match(/GMT([+-]\d{2}:\d{2})?/);
    return m ? (m[1] || '+00:00') : '';
  } catch {
    return '';
  }
}

/**
 * Menu del fuso orario di un utente: casella con ricerca invece di un <select> nativo (centinaia
 * di nomi IANA, difficili da scorrere a mano), con lo scarto GMT accanto a ciascun fuso.
 * Vuoto = «Come l'azienda»: segue il fuso dell'azienda anche se cambia.
 * `companyZone` è il fuso dell'azienda (company_timezone nel profilo, timezone nell'elenco aziende).
 */
export default function KernelTimezoneSelect({ id, value, onChange, companyZone, noFollow = false, hint }) {
  const { t } = useLanguage();
  const zones = useMemo(() => timezoneOptions(value), [value]);
  const followLabel = t('timezone.followCompany', { zone: companyZone || DEFAULT_TIMEZONE });
  const currentLabel = value || (noFollow ? '' : followLabel);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Il menu è in posizione fissa (calcolata sulla casella): così non viene tagliato dalle sezioni con
  // overflow nascosto, e si apre verso l'alto se sotto non c'è spazio.
  useLayoutEffect(() => {
    if (!open || !fieldRef.current) { setPos(null); return undefined; }
    const place = () => {
      const r = fieldRef.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const up = below < 200 && above > below;
      setPos({ left: r.left, width: r.width, maxHeight: Math.max(120, Math.min(260, up ? above : below)), ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    };
    place();
    const onScroll = (e) => { if (!(e.target && e.target.closest && e.target.closest('.kts-list'))) place(); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', place); };
  }, [open]);

  // All'apertura l'elenco parte dalla voce attuale (quella mostrata nella casella), non dal primo fuso.
  const listRef = useRef(null);
  const hasPos = pos !== null;
  useLayoutEffect(() => {
    const list = listRef.current;
    const current = list && list.querySelector('.is-selected');
    if (list && current && !query) list.scrollTop = current.offsetTop - 4;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPos]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const q = query.trim().toLowerCase();
  const matchFollow = !noFollow && (!q || followLabel.toLowerCase().includes(q));
  const matchedZones = q ? zones.filter((zone) => zone.toLowerCase().includes(q)) : zones;

  const select = (zone) => {
    onChange(zone);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="kts" ref={wrapperRef}>
      <div
               ref={fieldRef}
               className={`kts-field${open ? ' is-open' : ''}`}
               onMouseDown={(e) => {
                 if (e.target === inputRef.current) return;
                 e.preventDefault();
                 if (open) { setOpen(false); setQuery(''); inputRef.current?.blur(); } else { setOpen(true); setQuery(''); inputRef.current?.focus(); }
               }}
             >
        <input
          id={id}
          type="text"
          value={open ? query : currentLabel}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={(e) => {
            setOpen(true);
            setQuery('');
            e.target.select();
          }}
          placeholder={noFollow ? '' : followLabel}
          autoComplete="off"
        />
        <svg className="kts-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {open && pos && (
        <ul className="kts-list" style={pos} ref={listRef}>
          {matchFollow && (
            <li
              className={!value ? 'is-selected' : ''}
              onMouseDown={(e) => { e.preventDefault(); select(''); }}
            >
              <span>{followLabel}</span>
              <span className="kts-offset">{zoneOffsetText(companyZone || DEFAULT_TIMEZONE)}</span>
            </li>
          )}
          {matchedZones.map((zone) => (
            <li
              key={zone}
              className={value === zone ? 'is-selected' : ''}
              onMouseDown={(e) => { e.preventDefault(); select(zone); }}
            >
              <span>{zone}</span>
              <span className="kts-offset">{zoneOffsetText(zone)}</span>
            </li>
          ))}
          {matchedZones.length === 0 && !matchFollow && (
            <li className="kts-empty">{t('timezone.noResults')}</li>
          )}
        </ul>
      )}
      <span className="page-subtitle">{hint === undefined ? t('timezone.hint') : hint}</span>
    </div>
  );
}
